// PO Spec §4 (2026-06-21) — HR Oversight Dashboard.
//
// "Exception management" surface: a single landing page that
// surfaces the four spec case buckets (On Track / Due Soon /
// Overdue / Paused), the top-50 unacknowledged flag feed
// (anonymised for Amber/Red distress), and a per-manager load
// view. The legacy four-card menu (Cases / Members / Policies /
// Integrations) is gone (PO follow-up) — it duplicated the
// persistent HR nav (`HrShell.tsx`'s pill top nav at `md`, full
// sidebar at `lg`), which is on screen at every width this page
// renders (below `md`, `/hr` renders `HrCompactOverview` instead,
// which never had these cards).
//
// Privacy boundary (Phase 2.5): the API deliberately does not
// return per-employee PII. The dashboard's UI must not invent
// fields the server didn't send — there is no per-employee name
// next to a flag, no time-on-page, no engagement frequency. The
// structural guarantee is the API contract; this component is
// the type-level backstop.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { CASE_BUCKET_LABELS } from '@besideu/shared/cases';
import type { CaseSummary, FlagType } from '@besideu/shared/cases';
import type {
  AmberAggregate,
  EngagementAggregate,
  FrameworkMetrics,
  FrameworkStageCount,
  ManagerLoadRow,
  PreferencesAggregate,
} from '@besideu/shared/hr-dashboard';
import { csvRow, CSV_BOM } from '@besideu/shared/csv';
import { firstNameFromSession } from '@/lib/format.ts';
import { api, ApiError } from '@/lib/api.ts';
import { useSession } from '@/lib/auth.ts';
import { Button } from '@/components/ui/Button.tsx';
import { StatusDot, StatusPill, ToneFor, humaniseStatus } from '@/components/ui/StatusPill.tsx';
import { AmberAggregateTile } from '@/components/AmberAggregateTile.tsx';
import { EngagementAggregateTile } from '@/components/EngagementAggregateTile.tsx';
import { PreferencesAggregateTile } from '@/components/PreferencesAggregateTile.tsx';
import { TabList, TabPanel, useTabInSearchParam, type TabDef } from '@/components/ui/Tabs.tsx';

interface HrDashboardData {
  buckets: { onTrack: number; dueSoon: number; overdue: number; paused: number };
  flags: HrFlagRow[];
  // H7 — `managers` is omitted for HR_Viewer. The server
  // applies a k-anonymity floor (≥3 open cases) so a small
  // org with two managers and three cases each can't be
  // fingerprinted via the per-manager breakdown. Each row
  // carries an `adherence` figure (Phase 4 §3) — null below
  // the floor / when nothing is due yet.
  managers?: ManagerLoadRow[];
  flagKinds: readonly FlagType[];
  // H8 — `role` is the HR role that gated the response. The
  // UI uses this to hide the "Open pathway" deep-link for
  // HR_Viewer (who doesn't get `caseId` on flag rows either).
  role: 'HR_Admin' | 'HR_Viewer';
  // PO Spec 2026-06-21 (post-M3 review) — anonymised wellbeing
  // aggregate (Amber + Red). Same shape for HR_Admin and HR_Viewer.
  amberAggregate: AmberAggregate;
  // PO Spec Phase 4 §3 (2026-06-21) — engagement aggregate (Support
  // Space Activated + Resources Accessed). Same shape for HR_Admin
  // and HR_Viewer — both roles see the counts. k-anonymity floor
  // applied server-side; the tile only reads structured fields.
  engagementAggregate: EngagementAggregate;
  // Vision & Strategy §5/§6 — support-preferences aggregate. Counts only,
  // k-anon-gated. Same shape for HR_Admin and HR_Viewer.
  preferencesAggregate: PreferencesAggregate;
  // Support Framework spec §9 — org framework oversight metrics (operational).
  frameworkMetrics: FrameworkMetrics;
}

export interface HrFlagRow {
  id: string;
  // H8 — `caseId` is omitted for HR_Viewer. The deep-link to
  // the case detail page is only available to HR_Admin; the
  // Viewer sees a flag row with no clickable case attribution.
  caseId?: string;
  flagType: FlagType;
  message: string | null;
  severity: 'info' | 'warning' | 'critical';
  createdAt: string;
  acknowledgedAt: string | null;
}

// Client-side only — `data` is already the full aggregate payload the
// dashboard query holds in memory (all of it k-anon-gated server-side
// before it ever reached the browser), so this needs no new backend call.
// The one place a Blob + programmatic-anchor-click is actually warranted in
// this app (unlike the `/hr/cases` export, which downloads a server
// response via a plain `<a href>`): there's no server response to link to
// here, the CSV is generated in the browser from data already fetched.
function downloadDashboardReport(data: HrDashboardData) {
  const lines: string[] = [
    csvRow(['Framework metrics', '']),
    csvRow(['Touch points due this week', data.frameworkMetrics.touchPointsDueThisWeek]),
    csvRow(['Overdue touch points', data.frameworkMetrics.overdueTouchPoints]),
    csvRow([
      'Touch points completed',
      `${data.frameworkMetrics.completion.met}/${data.frameworkMetrics.completion.evaluable}`,
    ]),
    csvRow(['Paused pathways', data.frameworkMetrics.pausedPathways]),
    csvRow([]),
    csvRow(['Support pathway stage', 'Scheduled', 'Due soon', 'Overdue', 'Completed']),
    ...data.frameworkMetrics.stageBreakdown.map((s) =>
      csvRow([
        `Stage ${String(s.stage).padStart(2, '0')} · ${s.name}`,
        s.scheduled,
        s.dueSoon,
        s.overdue,
        s.completed,
      ]),
    ),
    csvRow([]),
    csvRow(['Loss type', 'Count']),
    ...data.frameworkMetrics.lossTypeBreakdown.map((r) => csvRow([r.label, r.count])),
    csvRow([]),
    csvRow(['Support pathway buckets', '']),
    csvRow(['On track', data.buckets.onTrack]),
    csvRow(['Due soon', data.buckets.dueSoon]),
    csvRow(['Overdue', data.buckets.overdue]),
    csvRow(['Paused', data.buckets.paused]),
    csvRow([]),
    csvRow([
      'Amber signals (90d)',
      data.amberAggregate.amber.suppressed
        ? 'Suppressed (below reporting threshold)'
        : data.amberAggregate.amber.total,
    ]),
    csvRow([
      'Red signals (90d)',
      data.amberAggregate.red.suppressed
        ? 'Suppressed (below reporting threshold)'
        : data.amberAggregate.red.total,
    ]),
    csvRow([]),
    csvRow([
      'Support space activated',
      data.engagementAggregate.supportSpaceActivated.suppressed
        ? 'Suppressed (below reporting threshold)'
        : data.engagementAggregate.supportSpaceActivated.total,
    ]),
    csvRow([
      'Resources accessed',
      data.engagementAggregate.resourcesAccessed.suppressed
        ? 'Suppressed (below reporting threshold)'
        : data.engagementAggregate.resourcesAccessed.total,
    ]),
  ];
  // `managers` is omitted from the payload entirely for HR_Viewer (server
  // side, per H7) — this section just doesn't exist for them, no extra
  // gating needed here. It can still be a present-but-empty array for an
  // HR_Admin org with no cases yet (e.g. right after onboarding), so guard
  // on length too — otherwise an orphan header row appears with no data
  // beneath it.
  if (data.managers && data.managers.length > 0) {
    lines.push(csvRow([]), csvRow(['Manager', 'On track', 'Due soon', 'Overdue', 'Adherence']));
    for (const m of data.managers) {
      lines.push(
        csvRow([
          m.managerName,
          m.onTrack,
          m.dueSoon,
          m.overdue,
          m.adherence ? `${Math.round(m.adherence.rate * 100)}%` : '',
        ]),
      );
    }
  }

  const blob = new Blob([CSV_BOM + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `besideu-dashboard-report-${new Date().toISOString().slice(0, 10)}.csv`;
  // Safari requires the anchor to be attached to the DOM for a synthetic
  // click to reliably trigger a download, and can race a synchronous
  // revoke against its own async download handling — attach, click,
  // detach, and defer the revoke to the next tick.
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function HrDashboard() {
  const session = useSession('hr-shell');
  const firstName = firstNameFromSession(session as { name?: string });
  const data = useQuery({
    queryKey: ['hr', 'dashboard'],
    queryFn: () => api.get<HrDashboardData>('/api/hr/dashboard'),
    // The dashboard is the HR's primary landing — refetch on focus
    // so a new "case created" notification surfaces within seconds
    // (the 10s contract from PO §2.5).
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  return (
    <section className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
          <p className="mt-2 text-muted-foreground">
            {firstName
              ? `Welcome back, ${firstName}. Here's the shape of bereavement support in your organisation today.`
              : 'Welcome back. Here\u2019s the shape of bereavement support in your organisation today.'}
          </p>
        </div>
        <Button
          variant="secondary"
          className="shrink-0"
          disabled={!data.data}
          onClick={() => data.data && downloadDashboardReport(data.data)}
        >
          Download report
        </Button>
      </header>

      {data.isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : data.error ? (
        <p className="text-traffic-red">Could not load the dashboard.</p>
      ) : data.data ? (
        <DashboardSurface data={data.data} />
      ) : null}
    </section>
  );
}

// Three tabs, splitting along the same line already validated at phone width
// (`HrCompactOverview`): "Overview" is the exception-management surface with
// a time-bound safety duty (acknowledging a flag within 24h), so it stays
// the default tab rather than being one click further away than before.
// "Insights" and "Team" are the "analysis surfaces that want a big screen"
// the compact view already drops — same content, just no longer all in one
// scroll. The Team tab only exists for HR_Admin (H7 — `managers` is omitted
// server-side for HR_Viewer), so the tab list itself is built from `data`
// rather than hardcoded, keeping `useTabInSearchParam`'s id list in sync
// with what's actually rendered.
const OVERVIEW_TAB = { id: 'overview', label: 'Overview' } as const;
const INSIGHTS_TAB = { id: 'insights', label: 'Insights' } as const;
const TEAM_TAB = { id: 'team', label: 'Team' } as const;

function DashboardSurface({ data }: { data: HrDashboardData }) {
  // `data.managers` can be a present-but-empty array (an HR_Admin org with no
  // open cases, e.g. brand new or everything Closed) — truthy but nothing to
  // show, so gate on length, not just presence, or the Team tab renders dead
  // (`ManagerLoad` itself returns null for []). Mirrors the same guard in
  // `downloadDashboardReport` above.
  const managers = data.managers && data.managers.length > 0 ? data.managers : null;
  const tabs: TabDef[] = managers
    ? [OVERVIEW_TAB, INSIGHTS_TAB, TEAM_TAB]
    : [OVERVIEW_TAB, INSIGHTS_TAB];
  const tabIds = tabs.map((t) => t.id);
  const [active, setActive] = useTabInSearchParam('tab', tabIds, OVERVIEW_TAB.id);

  return (
    <div className="space-y-6">
      <TabList tabs={tabs} active={active} onChange={setActive} aria-label="HR dashboard" />

      <TabPanel id={OVERVIEW_TAB.id} active={active === OVERVIEW_TAB.id}>
        <div className="grid gap-3 sm:grid-cols-4">
          <BucketTile
            label={CASE_BUCKET_LABELS.onTrack}
            count={data.buckets.onTrack}
            tone="ok"
            to="/hr/cases?bucket=onTrack"
          />
          <BucketTile
            label={CASE_BUCKET_LABELS.dueSoon}
            count={data.buckets.dueSoon}
            tone="warn"
            to="/hr/cases?bucket=dueSoon"
          />
          <BucketTile
            label={CASE_BUCKET_LABELS.overdue}
            count={data.buckets.overdue}
            tone="alert"
            to="/hr/cases?bucket=overdue"
          />
          <BucketTile
            label={CASE_BUCKET_LABELS.paused}
            count={data.buckets.paused}
            tone="muted"
            to="/hr/cases?bucket=paused"
          />
        </div>

        {/* Journey 05 — the actual active support pathways at a glance
            ("clarity"), leading the exception feed below. A preview of the
            full /hr/cases list. */}
        <ActiveCasesPreview />

        <FlagFeed flags={data.flags} />
      </TabPanel>

      <TabPanel id={INSIGHTS_TAB.id} active={active === INSIGHTS_TAB.id}>
        {/* §6.4 anonymised wellbeing aggregate (PO 2026-06-21).
            First-class tile so HR can see the trend without
            navigating to the HR-Maeve chat. Same shape for
            HR_Admin and HR_Viewer. */}
        <AmberAggregateTile data={data.amberAggregate} />

        {/* PO Spec Phase 4 §3 (2026-06-21) — engagement aggregate
            (Support Space Activated + Resources Accessed). Same
            shape for HR_Admin and HR_Viewer — both roles see the
            counts. The tile is the §3.3 "Privacy-First Analytics"
            surface: HR can see that engagement is happening
            without ever seeing Maeve content or per-kind resource
            attribution. */}
        <EngagementAggregateTile data={data.engagementAggregate} />

        <PreferencesAggregateTile data={data.preferencesAggregate} />

        {/* Support Framework spec §9 — framework oversight metrics. Operational
            workload + adherence + loss-type routing; never judgemental. */}
        <FrameworkMetricsTile data={data.frameworkMetrics} />
      </TabPanel>

      {/* H7 — per-manager breakdown is HR_Admin only. The server applies a
          k-anonymity floor (≥3 open cases) and rolls sub-floor managers into
          "Other managers"; HR_Viewer doesn't get this tab at all. */}
      {managers ? (
        <TabPanel id={TEAM_TAB.id} active={active === TEAM_TAB.id}>
          <ManagerLoad managers={managers} />
        </TabPanel>
      ) : null}
    </div>
  );
}

// Journey 05 — "Active cases" preview. A snapshot of the open cases (the
// most attention-needing first), each one tap from the case detail, with a
// "View all" into the full /hr/cases list. Read-only for both HR roles.
//
// `status=Open` (server-side filter, not client-side) + a generous `limit`:
// GET /api/hr/cases defaults to 50 rows ordered by `createdAt DESC` for the
// paginated /hr/cases list. Without an explicit filter here, an org with
// >50 cases of any status would only see its NEWEST 50 — and overdue cases
// are old by definition, so exactly the ones this preview exists to surface
// could silently fall outside that window. Filtering server-side to Open
// avoids that: Open-case volume for a single org is realistically bounded
// (bereavement cases, not headcount), so 200 is a safe, generous ceiling —
// same posture as the manager-roster `limit=200` elsewhere in this app.
function ActiveCasesPreview() {
  const cases = useQuery({
    queryKey: ['hr', 'cases', { status: 'Open', preview: true }],
    queryFn: () => api.get<{ cases: CaseSummary[] }>('/api/hr/cases?status=Open&limit=200'),
    // Match the dashboard-data query's freshness (PO §2.5) so the preview rows
    // don't lag the tiles/flags above them.
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  return (
    <section className="rounded-lg border border-border bg-background p-6">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Active support pathways</h2>
        <Link
          to="/hr/cases"
          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          View all
        </Link>
      </header>

      {cases.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : cases.error ? (
        <p className="text-sm text-muted-foreground">Could not load support pathways right now.</p>
      ) : (
        <ActiveCasesTable cases={cases.data?.cases ?? []} />
      )}
    </section>
  );
}

function ActiveCasesTable({ cases }: { cases: CaseSummary[] }) {
  // Open support pathways only, most attention-needing first (overdue first
  // response, then overdue touch point), then most-recent loss. Preview the
  // top few; the rest live on /hr/cases.
  const active = cases.filter((c) => c.status === 'Open');
  const ranked = [...active]
    .sort(
      (a, b) => attentionScore(b) - attentionScore(a) || b.dateOfLoss.localeCompare(a.dateOfLoss),
    )
    .slice(0, 6);

  if (active.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No active support pathways right now. New ones appear here as managers open them.
      </p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Active bereavement support pathways</caption>
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="pr-4 pb-2 font-medium">Employee</th>
              <th className="pr-4 pb-2 font-medium">Loss</th>
              <th className="pr-4 pb-2 font-medium">Status</th>
              <th className="pb-2 font-medium">Check-in</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((c) => {
              const check = checkInStatus(c);
              return (
                <tr key={c.id} className="border-t border-border">
                  <td className="py-2.5 pr-4">
                    <Link to={`/hr/cases/${c.id}`} className="font-medium hover:underline">
                      {c.employee.name}
                    </Link>
                    <span className="block text-xs text-muted-foreground">
                      Mgr: {c.manager.name}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-muted-foreground">
                    {c.deceasedRelationship} · {c.dateOfLoss}
                  </td>
                  <td className="py-2.5 pr-4">
                    <StatusPill tone={ToneFor(c.status)} dot>
                      {humaniseStatus(c.status)}
                    </StatusPill>
                  </td>
                  <td className="py-2.5">
                    <StatusPill tone={check.tone} dot>
                      {check.label}
                    </StatusPill>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {active.length > ranked.length ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Showing {ranked.length} of {active.length} active support pathways.{' '}
          <Link to="/hr/cases" className="text-primary underline-offset-4 hover:underline">
            View all
          </Link>
          .
        </p>
      ) : null}
    </>
  );
}

// Attention ranking: overdue first response is the most urgent oversight
// signal (§6.4 — an employee not yet contacted), then an overdue touch point.
function attentionScore(c: CaseSummary): number {
  return (c.hasOverdueFirstResponse ? 2 : 0) + (c.hasOverdueTouchpoint ? 1 : 0);
}

function checkInStatus(c: CaseSummary): { label: string; tone: 'positive' | 'danger' } {
  if (c.hasOverdueFirstResponse) return { label: 'No response', tone: 'danger' };
  if (c.hasOverdueTouchpoint) return { label: 'Overdue', tone: 'danger' };
  return { label: 'On track', tone: 'positive' };
}

function BucketTile({
  label,
  count,
  tone,
  to,
}: {
  label: string;
  count: number;
  tone: 'ok' | 'warn' | 'alert' | 'muted';
  to: string;
}) {
  // Tone is carried by the border + tinted fill; the count/label text stays
  // `text-foreground` (navy) so it clears WCAG 2.2 AA — the traffic-amber /
  // -green tokens are mid-lightness and fail as foreground on a near-white
  // fill. `alert` keeps red text: it is the design's deliberate "overdue"
  // emphasis and, as large text, clears the 3:1 large-text threshold.
  const toneClass =
    tone === 'alert'
      ? 'border-traffic-red/40 bg-traffic-red/5 text-traffic-red'
      : tone === 'warn'
        ? 'border-traffic-amber/40 bg-traffic-amber/10 text-foreground'
        : tone === 'muted'
          ? 'border-border bg-muted/30 text-muted-foreground'
          : 'border-traffic-green/40 bg-traffic-green/10 text-foreground';
  return (
    <Link to={to} className={`block rounded-lg border p-4 transition hover:shadow-sm ${toneClass}`}>
      <p className="text-3xl font-semibold tabular-nums">{count}</p>
      <p className="mt-1 text-sm font-medium">{label}</p>
    </Link>
  );
}

// Exported — `HrCompactOverview` renders the SAME feed at phone width. The
// flag feed is the one part of this dashboard that carries a time-bound
// safety duty (a red flag must be reviewed within 24h), so it is deliberately
// NOT among the things the compact overview drops. One implementation so the
// acknowledge action can't drift between the two.
export function FlagFeed({ flags }: { flags: HrFlagRow[] }) {
  const queryClient = useQueryClient();
  const ack = useMutation({
    mutationFn: (flagId: string) => api.post<{ ok: true }>(`/api/hr/dashboard/flags/${flagId}/ack`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hr', 'dashboard'] }),
  });

  if (flags.length === 0) {
    return (
      <section className="rounded-lg border border-border bg-background p-6">
        <h2 className="text-base font-semibold">No active flags</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          When something needs your attention — a new support pathway, an overdue touch point, a
          manager who's gone quiet — it will appear here.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-border bg-background p-6">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Needs your attention</h2>
        <p className="text-xs text-muted-foreground">{flags.length} active · grouped by severity</p>
      </header>
      <ul className="space-y-2">
        {flags.map((f) => (
          <li
            key={f.id}
            className={`flex flex-wrap items-start justify-between gap-3 rounded-md border p-3 ${severityClass(f.severity)}`}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{labelForFlag(f.flagType)}</p>
              {f.message ? (
                <p className="mt-0.5 text-xs text-muted-foreground">{f.message}</p>
              ) : null}
              <p className="mt-1 text-xs text-muted-foreground">
                {new Date(f.createdAt).toLocaleString('en-GB', { timeZone: 'Europe/London' })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* H8 — HR_Viewer doesn't get a `caseId` on the flag
                  row, so the deep-link to the case detail page
                  is gated on `caseId` being present. The
                  Viewer's flag feed is anonymised: counts +
                  flag-kind label + the operator-action button
                  (Acknowledge). For deeper triage the Viewer
                  navigates to /hr/cases and filters there. */}
              {f.caseId ? (
                <Link
                  to={`/hr/cases/${f.caseId}`}
                  className="rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
                >
                  Open pathway
                </Link>
              ) : null}
              {f.acknowledgedAt ? (
                <span className="text-xs text-muted-foreground" aria-label="Acknowledged">
                  ✓ acknowledged
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => ack.mutate(f.id)}
                  disabled={ack.isPending}
                  className="rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
                >
                  Acknowledge
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {ack.error ? <AckError error={ack.error} /> : null}
    </section>
  );
}

function severityClass(severity: HrFlagRow['severity']): string {
  if (severity === 'critical') return 'border-traffic-red/40 bg-traffic-red/5';
  if (severity === 'warning') return 'border-traffic-amber/40 bg-traffic-amber/8';
  return 'border-border bg-muted/30';
}

function labelForFlag(kind: FlagType): string {
  switch (kind) {
    case 'Case_Created':
      return 'New support pathway';
    case 'First_Response_Sent':
      return 'First response sent';
    case 'Manager_Inactivity':
    case 'Missing_Manager_Response':
      return 'Manager inactivity';
    case 'Overdue_Touchpoint':
      return 'Overdue touch point';
    case 'Consecutive_Misses':
      return 'Consecutive missed touch points';
    case 'RTW_Plan_Unacknowledged':
      return 'RTW plan unacknowledged';
    case 'Amber_Maeve':
    case 'Distress_Amber':
      return 'Amber distress signal';
    case 'Red_Maeve':
    case 'Distress_Red':
      return 'Red distress signal';
    case 'RTW_Date_Review':
      return 'RTW date — review support dates';
    case 'Loss_Type_Review':
      return 'Loss type — review support';
    case 'Manager_Deactivated':
      return 'Manager deactivated — needs reassignment';
    case 'Manager_Role_Changed':
      return 'Manager role changed — needs reassignment';
  }
}

function AckError({ error }: { error: unknown }) {
  const message = error instanceof ApiError ? error.message : 'unknown_error';
  return (
    <p className="mt-2 text-sm text-traffic-red" role="alert">
      Could not acknowledge: {message}
    </p>
  );
}

// Shared horizontal stacked bar — used by `ManagerLoad`'s onTrack/dueSoon/
// overdue breakdown and `StageBreakdown`'s completed/dueSoon/overdue/
// scheduled breakdown, previously two independent copies of the same
// `bg-muted` track + `flex h-full` + per-segment `width`/`title` markup.
// Colour is never the sole signal: every segment carries a `title`, and
// callers render the raw counts as text beside the bar.
function SegmentedBar({
  segments,
}: {
  segments: { value: number; className: string; title: string }[];
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  return (
    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
      <div className="flex h-full">
        {segments.map((s, i) =>
          s.value > 0 ? (
            <div
              key={i}
              className={s.className}
              style={{ width: `${(s.value / total) * 100}%` }}
              title={s.title}
            />
          ) : null,
        )}
      </div>
    </div>
  );
}

function ManagerLoad({ managers }: { managers: ManagerLoadRow[] }) {
  // H7 — "Other managers" is the rolled-up bucket the server
  // created for sub-floor managers. The deep-link is omitted
  // (no single case to filter on).
  const otherManagers = managers.find((m) => m.managerId === '__other__');
  const individualManagers = managers.filter((m) => m.managerId !== '__other__');
  const renderManagers = otherManagers
    ? [...individualManagers, otherManagers]
    : individualManagers;
  if (managers.length === 0) return null;
  const max = Math.max(1, ...renderManagers.map((m) => m.onTrack + m.dueSoon + m.overdue));
  return (
    <section className="rounded-lg border border-border bg-background p-6">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Manager load &amp; adherence</h2>
        <p className="text-xs text-muted-foreground">
          On-time = due touch points actioned within the grace window
        </p>
      </header>
      <ul className="space-y-2">
        {renderManagers.map((m) => {
          const total = m.onTrack + m.dueSoon + m.overdue;
          const width = `${(total / max) * 100}%`;
          const isOther = m.managerId === '__other__';
          return (
            <li key={m.managerId} className="flex flex-wrap items-center gap-3">
              {/* H7 — "Other managers" is non-clickable; the
                  k-anonymity rollup prevents per-individual
                  attribution. */}
              {isOther ? (
                <span className="min-w-[140px] text-sm font-medium text-muted-foreground">
                  {m.managerName}
                </span>
              ) : (
                <Link
                  to={`/hr/cases?manager=${m.managerId}`}
                  className="min-w-[140px] text-sm font-medium underline-offset-2 hover:underline"
                >
                  {m.managerName}
                </Link>
              )}
              <SegmentedBar
                segments={[
                  {
                    value: m.onTrack,
                    className: 'bg-traffic-green',
                    title: `${m.onTrack} on track`,
                  },
                  {
                    value: m.dueSoon,
                    className: 'bg-traffic-amber',
                    title: `${m.dueSoon} due soon`,
                  },
                  { value: m.overdue, className: 'bg-traffic-red', title: `${m.overdue} overdue` },
                ]}
              />
              <span className="min-w-[3rem] text-right text-xs text-muted-foreground tabular-nums">
                {width}
              </span>
              {/* Phase 4 §3 — per-manager adherence to the support
                  framework. null on the "Other managers" rollup and
                  when nothing has come due yet (see AdherenceBadge). */}
              <AdherenceBadge adherence={m.adherence} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// Phase 4 §3 — on-time touch point adherence for a single manager. `null` means
// either the manager has no touch point due yet, or this is the k-anon "Other
// managers" rollup (where a single sub-floor manager's rate would
// de-anonymise them) — both render a neutral em-dash, never a misleading 0%.
function AdherenceBadge({ adherence }: { adherence: ManagerLoadRow['adherence'] }) {
  if (!adherence) {
    return (
      <span
        className="min-w-[7rem] text-right text-xs text-muted-foreground"
        title="No touch points have come due for this manager yet."
      >
        — on time
      </span>
    );
  }
  const pct = Math.round(adherence.rate * 100);
  // Colour signal via the dot (the traffic-* text tokens are too light to
  // clear WCAG AA as small foreground text); the percentage itself stays
  // `text-foreground`. The dot is not the sole signal — the number carries it.
  const dotTone =
    adherence.rate >= 0.8 ? 'positive' : adherence.rate >= 0.5 ? 'attention' : 'danger';
  return (
    <span
      className="inline-flex min-w-[7rem] items-center justify-end gap-1.5 text-xs font-medium text-foreground tabular-nums"
      title={`${adherence.met} of ${adherence.evaluable} due touch points actioned on time`}
    >
      <StatusDot tone={dotTone} />
      {pct}% on time
    </span>
  );
}

// Support Framework spec §9 — org framework oversight metrics. Operational
// (workload + adherence + loss-type routing), never judgemental: no grief
// scores, no per-employee attribution, loss types k-anon-gated server-side.
function FrameworkMetricsTile({ data }: { data: FrameworkMetrics }) {
  const pct =
    data.completion.evaluable > 0
      ? Math.round((data.completion.met / data.completion.evaluable) * 100)
      : null;
  return (
    <section className="rounded-lg border border-border p-6">
      <h2 className="text-base font-semibold">Framework oversight</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        An operational view of the 12-month support framework across open support pathways.
      </p>
      <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FrameworkMetric label="Touch points due this week" value={data.touchPointsDueThisWeek} />
        <FrameworkMetric label="Overdue touch points" value={data.overdueTouchPoints} />
        <FrameworkMetric
          label="Framework completion"
          value={pct === null ? '—' : `${pct}%`}
          hint={
            pct === null
              ? 'nothing due yet'
              : `${data.completion.met} of ${data.completion.evaluable}`
          }
        />
        <FrameworkMetric label="Paused pathways" value={data.pausedPathways} />
      </dl>

      {/* PO follow-up ("doesn't show the 7 step support pathway") — the
          framework's own stages, not just the flat numbers above. Same
          stage list + names as the per-case timeline (`CaseDetailView.tsx`'s
          `SUPPORT_FRAMEWORK_STAGES.map`), aggregated to touch-point counts
          across every open pathway in the org. */}
      <div className="mt-6">
        <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Support pathway — 7-stage framework
        </p>
        <StageBreakdown stages={data.stageBreakdown} />
      </div>

      {data.lossTypeBreakdown.length > 0 ? (
        <div className="mt-5">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Loss type breakdown
          </p>
          <ul className="mt-2 flex flex-wrap gap-2 text-sm">
            {data.lossTypeBreakdown.map((r) => (
              <li key={r.label} className="rounded-full bg-muted px-3 py-1">
                {r.label}: {r.count}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// PO follow-up ("doesn't show the 7 step support pathway") — one row per
// framework stage, each a stacked bar of its touch points' display statuses
// (§7 vocabulary — same colours as `ManagerLoad`'s bars above: green
// completed, amber due soon, red overdue). The fourth, colour-neutral
// segment (`bg-border`) is "scheduled, not yet due" — deliberately not one
// of the traffic colours, since it isn't a signal that needs attention.
// Colour is never the sole signal: each segment carries a `title` and the
// stage's raw total renders as text beside the bar.
function StageBreakdown({ stages }: { stages: FrameworkStageCount[] }) {
  return (
    <ol className="mt-2 space-y-2">
      {stages.map((stage) => {
        const total = stage.completed + stage.dueSoon + stage.overdue + stage.scheduled;
        return (
          <li key={stage.stage} className="flex flex-wrap items-center gap-3">
            <span className="min-w-[180px] text-sm">
              <span className="font-medium">Stage {String(stage.stage).padStart(2, '0')}</span>{' '}
              <span className="text-muted-foreground">· {stage.name}</span>
            </span>
            {total === 0 ? (
              <span className="text-xs text-muted-foreground">No touch points yet</span>
            ) : (
              <>
                <SegmentedBar
                  segments={[
                    {
                      value: stage.completed,
                      className: 'bg-traffic-green',
                      title: `${stage.completed} completed`,
                    },
                    {
                      value: stage.dueSoon,
                      className: 'bg-traffic-amber',
                      title: `${stage.dueSoon} due soon`,
                    },
                    {
                      value: stage.overdue,
                      className: 'bg-traffic-red',
                      title: `${stage.overdue} overdue`,
                    },
                    {
                      value: stage.scheduled,
                      className: 'bg-border',
                      title: `${stage.scheduled} scheduled`,
                    },
                  ]}
                />
                <span className="min-w-[3rem] text-right text-xs text-muted-foreground tabular-nums">
                  {total}
                </span>
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function FrameworkMetric({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string | undefined;
}) {
  return (
    <div>
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
